import {Component, inject, ChangeDetectionStrategy} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, switchMap, take} from 'rxjs/operators';
import {VwuiAngularModule, VwuiModalService} from '@bvgo-ict/vwui-angular';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ProjectLinkService, ToastService} from '../../services';
import {CreateUpdateProjectLinkModalComponent, DeleteItemModalComponent} from '../../components';
import {ProjectLink} from '../../models/project-analysis';

@Component({
    selector: 'app-page-project-links',
    templateUrl: './page-project-links.component.html',
    styleUrls: ['./page-project-links.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TranslatePipe, VwuiAngularModule]
})
export class PageProjectLinksComponent {
    private readonly projectLinkService = inject(ProjectLinkService);
    private readonly modalService = inject(VwuiModalService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);

    public readonly links = toSignal(this.projectLinkService.links$);

    public openCreateModal(): void {
        this.openModal().pipe(
            switchMap((link) => this.projectLinkService.add(link))
        ).subscribe(() => this.notifySuccess('PAGE_PROJECT_LINKS.link_added'));
    }

    public openUpdateModal(link: ProjectLink): void {
        this.openModal(link).pipe(
            switchMap((updated) => this.projectLinkService.update(link.id, updated))
        ).subscribe(() => this.notifySuccess('PAGE_PROJECT_LINKS.link_modified'));
    }

    public openDeleteModal(link: ProjectLink): void {
        const modal = this.modalService.open(DeleteItemModalComponent, {
            data: {
                title: 'PAGE_PROJECT_LINKS.delete_item.title',
                description: 'PAGE_PROJECT_LINKS.delete_item.description',
                extraDescription: null
            }
        });

        modal.afterClosed.pipe(
            take(1),
            filter(Boolean),
            switchMap(() => this.projectLinkService.delete(link.id))
        ).subscribe(() => this.notifySuccess('PAGE_PROJECT_LINKS.link_removed'));
    }

    private openModal(link?: ProjectLink) {
        const modal = this.modalService.open(CreateUpdateProjectLinkModalComponent, {
            closeOnBackdropClick: false,
            data: {
                title: link ? 'PAGE_PROJECT_LINKS.update_item.title' : 'PAGE_PROJECT_LINKS.create_item.title',
                saveText: link ? 'PAGE_PROJECT_LINKS.update_item.save' : 'PAGE_PROJECT_LINKS.create_item.save',
                data: link
            }
        });

        return modal.afterClosed.pipe(
            take(1),
            filter((result: ProjectLink | undefined): result is ProjectLink => !!result)
        );
    }

    private notifySuccess(translationKey: string): void {
        this.toastService.openSuccess(this.translateService.instant(translationKey));
    }
}
